// src/components/Auth/RegisterForm.jsx
import React, { useState, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authRateLimiter } from '../../utils/rateLimiter';
import { Clock, Check, Shield, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';

const RegisterForm = ({ onSuccess, onLoginClick }) => {
  const { signUp } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  // Memoize validation patterns
  const emailRegex = useMemo(() => 
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    []
  );

  const passwordRegex = useMemo(() => /^(?=.*[A-Za-z])(?=.*\d).+$/, []);

  // Memoize rate limit options
  const rateLimitOptions = useMemo(() => ({
    maxAttempts: 3,
    windowMs: 60 * 60 * 1000,
    lockoutMs: 24 * 60 * 60 * 1000
  }), []);

  // Memoize validation functions
  const validateEmail = useCallback((emailToValidate) => {
    return emailRegex.test(String(emailToValidate).toLowerCase());
  }, [emailRegex]);

  // Function to create profile with retry logic
  const createProfileWithRetry = useCallback(async (profileData, retries = 3) => {
    for (let i = 0; i < retries; i++) {
      if (i > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, i - 1)));
      }

      const { data: profileResult, error: profileError } = await supabase
        .from('business_profiles')
        .insert(profileData)
        .select()
        .single();

      if (!profileError) {
        return { success: true, data: profileResult };
      }

      if (profileError.code !== '42501') {
        console.error("Non-RLS error creating profile:", profileError);
        return { success: false, error: profileError };
      }
    }

    return { success: false, error: { message: "Failed to create profile after retries" } };
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    
    if (loading) {
      setError("Please wait a moment.");
      return;
    }
    
    // Enhanced validation
    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }
    
    if (!firstName.trim()) {
      setError("Please enter your first name");
      return;
    }
    
    if (!businessName.trim()) {
      setError("Please enter your business name");
      return;
    }
    
    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }
    
    if (password.length < 6) {
      setError("Password should be at least 6 characters");
      return;
    }
    
    if (!passwordRegex.test(password)) {
      setError("Password must contain at least one letter and one number");
      return;
    }

    // Check rate limiting
    const registrationKey = email || 'anonymous';
    if (!authRateLimiter.check(registrationKey, 'registration', rateLimitOptions)) {
      setError("Too many registration attempts. Please try again later.");
      return;
    }
    
    setLoading(true);

    try {
      // Create the user account (NO payment validation needed)
      const { data, error: signUpError } = await signUp(email, password, {
        data: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          businessName: businessName.trim()
        }
      });
      
      if (signUpError) throw signUpError;
      
      if (data?.user) {
        // Create profile with trial status (NO payment method required)
        const profileData = {
          user_id: data.user.id,
          business_name: businessName.trim(),
          first_name: firstName.trim(),
          last_name: lastName.trim() || '',
          subscription_status: 'trial',
          trial_start_date: new Date().toISOString(),
          trial_end_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
          has_used_trial: true,
          stripe_customer_id: null,  // No Stripe customer yet
          payment_method_status: null,  // No payment method yet
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        const profileResult = await createProfileWithRetry(profileData);
        
        if (!profileResult.success) {
          console.error("Failed to create profile after retries:", profileResult.error);
          
          // Try edge function as fallback
          try {
            const { error: edgeFunctionError } = await supabase.functions.invoke(
              'create-user-profile',
              { body: profileData }
            );
            
            if (edgeFunctionError) {
              console.error("Edge function profile creation failed:", edgeFunctionError);
            }
          } catch (edgeError) {
            console.error("Edge function exception:", edgeError);
          }
        }
        
        // Sync with ActiveCampaign
        try {
          const syncData = {
            userId: data.user.id,
            email: email,
            firstName: firstName.trim(),
            lastName: lastName.trim() || '',
            businessName: businessName.trim()
          };
          
          const { error: syncError } = await supabase.functions.invoke(
            'sync-new-user-to-activecampaign', 
            { body: syncData }
          );
          
          if (syncError) {
            console.error('ActiveCampaign sync error:', syncError);
          }
        } catch (syncException) {
          console.error('ActiveCampaign sync exception:', syncException);
        }
      }
      
      setMessage("Registration successful! Please check your email to confirm your account.");
      localStorage.setItem('registrationEmail', email);
      localStorage.removeItem('selectedPlan'); // Clear any stored plan
      if (onSuccess) onSuccess(email);

      authRateLimiter.reset(registrationKey, 'registration');
      
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [
    email, password, confirmPassword, firstName, lastName, businessName,
    validateEmail, passwordRegex, rateLimitOptions, signUp, 
    createProfileWithRetry, onSuccess, loading
  ]);

  // Memoize event handlers
  const handleEmailChange = useCallback((e) => setEmail(e.target.value), []);
  const handlePasswordChange = useCallback((e) => setPassword(e.target.value), []);
  const handleConfirmPasswordChange = useCallback((e) => setConfirmPassword(e.target.value), []);
  const handleFirstNameChange = useCallback((e) => setFirstName(e.target.value), []);
  const handleLastNameChange = useCallback((e) => setLastName(e.target.value), []);
  const handleBusinessNameChange = useCallback((e) => setBusinessName(e.target.value), []);
  const handleLoginClick = useCallback(() => onLoginClick(), [onLoginClick]);

  // Memoize loading button content
  const loadingContent = useMemo(() => {
    if (!loading) return 'Start Free Trial';
    
    return (
      <>
        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Starting Your Free Trial...
      </>
    );
  }, [loading]);

  return (
    <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden max-w-xl mx-auto">
      {/* Gradient border effect */}
      <div 
        className="absolute inset-0"
        style={{ 
          backgroundImage: 'linear-gradient(to bottom right, rgba(189, 180, 226, 0.1), rgba(44, 54, 94, 0.1), rgba(189, 180, 226, 0.1))' 
        }}
      ></div>
      
      {/* Content Container */}
      <div className="relative z-10 p-8 lg:p-10">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div 
            className="inline-flex items-center border border-green-200 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-4"
            style={{ 
              backgroundImage: 'linear-gradient(to right, rgb(240, 253, 244), rgb(236, 253, 245))' 
            }}
          >
            <Zap className="w-4 h-4 mr-2" />
            14-Day Free Trial - No Credit Card Required
          </div>
          <h2 className="text-2xl font-bold text-sws-navy mb-2">Create Your Account</h2>
          <p className="text-sws-slate">Start discovering your true profitability today</p>
        </div>
        
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-lg">
            <p>{error}</p>
          </div>
        )}
        
        {message && (
          <div className="bg-green-50 border-l-4 border-green-500 text-green-700 p-4 mb-6 rounded-lg">
            <p>{message}</p>
          </div>
        )}

        {/* Trial Benefits Section */}
        <div 
          className="rounded-xl p-4 mb-6"
          style={{ 
            backgroundImage: 'linear-gradient(to right, rgba(189, 180, 226, 0.1), rgba(44, 54, 94, 0.1))' 
          }}
        >
          <h3 className="font-semibold text-sws-navy mb-3 flex items-center">
            <Clock size={16} className="mr-2" />
            Your 14-Day Trial Includes:
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                <Check size={12} className="text-green-600" />
              </div>
              <span className="ml-2 text-sm text-sws-slate">Full platform access</span>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                <Check size={12} className="text-green-600" />
              </div>
              <span className="ml-2 text-sm text-sws-slate">Analyze up to 8 offerings</span>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                <Check size={12} className="text-green-600" />
              </div>
              <span className="ml-2 text-sm text-sws-slate">Unlimited PDF reports</span>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                <Check size={12} className="text-green-600" />
              </div>
              <span className="ml-2 text-sm text-sws-slate">Cancel anytime</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="first-name">
                First Name
              </label>
              <input
                id="first-name"
                type="text"
                value={firstName}
                onChange={handleFirstNameChange}
                className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sws-purple focus:border-transparent transition-all"
                placeholder="John"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="last-name">
                Last Name
              </label>
              <input
                id="last-name"
                type="text"
                value={lastName}
                onChange={handleLastNameChange}
                className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sws-purple focus:border-transparent transition-all"
                placeholder="Doe"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="business-name">
              Business Name
            </label>
            <input
              id="business-name"
              type="text"
              value={businessName}
              onChange={handleBusinessNameChange}
              className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sws-purple focus:border-transparent transition-all"
              placeholder="Acme Consulting"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="register-email">
              Email Address
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-sws-slate" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
              </div>
              <input
                id="register-email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                className="pl-10 w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sws-purple focus:border-transparent transition-all"
                placeholder="you@example.com"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="register-password">
              Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-sws-slate" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                id="register-password"
                type="password"
                value={password}
                onChange={handlePasswordChange}
                className="pl-10 w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sws-purple focus:border-transparent transition-all"
                placeholder="••••••••"
                required
              />
            </div>
            <p className="text-xs text-sws-slate mt-1 ml-1">Must be at least 6 characters with letters and numbers</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="confirm-password">
              Confirm Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-sws-slate" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                className="pl-10 w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sws-purple focus:border-transparent transition-all"
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          {/* Security Notice - Updated */}
          <div 
            className="rounded-lg p-4 border border-blue-200"
            style={{ 
              backgroundImage: 'linear-gradient(to right, rgb(239, 246, 255), rgb(238, 242, 255))' 
            }}
          >
            <div className="flex items-start">
              <Shield size={18} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-blue-800 font-medium">No credit card required</p>
                <p className="text-xs text-blue-700 mt-1">
                  You'll only be asked for payment details when your trial ends or if you choose to upgrade early
                </p>
              </div>
            </div>
          </div>
          
          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full text-white py-3 px-4 rounded-xl shadow-lg text-sm font-semibold hover:shadow-xl transform hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center group"
              style={{ 
                backgroundImage: 'linear-gradient(to right, #2c365e, #484d6d)' 
              }}
            >
              {loadingContent}
            </button>
          </div>
        </form>
        
        <div className="mt-8 text-center">
          <p className="text-sm text-sws-slate">
            Already have an account?{' '}
            <button 
              type="button"
              onClick={handleLoginClick}
              className="font-medium text-sws-purple hover:text-sws-navy transition-colors"
            >
              Sign in
            </button>
          </p>
        </div>
        
        <div className="mt-6 border-t border-gray-200 pt-6">
          <p className="text-xs text-sws-slate text-center">
            By creating an account, you agree to our{' '}
            <Link
              to="/terms"
              className="text-sws-purple hover:text-sws-navy"
            >
              Terms of Service
            </Link>
            {' '}and{' '}
            <Link
              to="/privacy"
              className="text-sws-purple hover:text-sws-navy"
            >
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default React.memo(RegisterForm);